import { Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { HttpClient } from '@angular/common/http';
import { MatSort } from '@angular/material/sort';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { AuthService } from '../services/auth.service';
import { first } from 'rxjs/operators';

export interface FileWithValue {
    file: any;
    value: string;
}

@Component({
    selector: 'app-multi-image-select-dialog',
    templateUrl: './multi-image-select-dialog.component.html',
    styleUrls: ['./multi-image-select-dialog.component.scss']
})
export class MultiImageSelectDialogComponent implements OnInit {
    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;

    dataSource: MatTableDataSource<any>;
    curpath = '/';
    selectedFiles: Set<any> = new Set();
    fileValues: Map<string, string> = new Map(); // Store values for each file by URL
    currentUser: any;
    curfiles: any[] = [];
    uploaddlg = false;

    constructor(
        public dialogRef: MatDialogRef<MultiImageSelectDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        public afs: AngularFirestore,
        private authService: AuthService,
        public http: HttpClient,
        private storage: AngularFireStorage,
        private snackBar: MatSnackBar
    ) {
        this.dataSource = new MatTableDataSource();
        this.dataSource.data = [];
    }

    async ngOnInit() {
        // Initialize with existing values if provided
        if (this.data?.existingOptions) {
            // GUI format
            this.initializeExistingValues(this.data.existingOptions);
        } else if (this.data?.existingValue) {
            // Editor format - current cell value
            this.initializeExistingValues(this.data.existingValue);
        }

        this.authService.authState.pipe(first()).toPromise().then(user => {
            this.currentUser = user;
            if (user) {
                this.curpath = '/';
                this.loadFiles(this.curpath);
            } else {
                console.error("MultiImageSelectDialog: User not logged in.");
            }
        });
    }

    private initializeExistingValues(existingData: any) {
        if (Array.isArray(existingData)) {
            // GUI format: array of { name: url, wert: value }
            existingData.forEach(option => {
                if (this.isImageUrl(option.name)) {
                    this.fileValues.set(option.name, option.wert || '');
                }
            });
        } else if (typeof existingData === 'string') {
            // Editor format: "URL(value);URL(value)" or "URL;URL"
            const items = existingData.split(';').filter(item => item.trim());
            items.forEach(item => {
                const trimmedItem = item.trim();
                if (trimmedItem.includes('(') && trimmedItem.includes(')')) {
                    // Format: "URL(value)"
                    const lastOpenParen = trimmedItem.lastIndexOf('(');
                    const lastCloseParen = trimmedItem.lastIndexOf(')');
                    if (lastOpenParen > 0 && lastCloseParen > lastOpenParen) {
                        const url = trimmedItem.substring(0, lastOpenParen);
                        const value = trimmedItem.substring(lastOpenParen + 1, lastCloseParen);
                        if (this.isImageUrl(url)) {
                            this.fileValues.set(url, value);
                        }
                    }
                } else {
                    // Format: just "URL"
                    if (this.isImageUrl(trimmedItem)) {
                        this.fileValues.set(trimmedItem, '');
                    }
                }
            });
        }
    }

    private isImageUrl(url: string): boolean {
        if (!url || typeof url !== 'string') return false;

        // Handle semicolon-separated URLs
        const urls = url.split(';').map(u => u.trim()).filter(u => u.length > 0);
        const firstUrl = urls[0];

        const firebaseUrlPattern = /https:\/\/firebasestorage\.googleapis\.com\/[^\s;]+/;
        const generalUrlPattern = /https?:\/\/[^\s;]+\.(png|jpg|jpeg|gif|svg|webp|PNG|JPG|JPEG|GIF|SVG|WEBP)/;

        return firebaseUrlPattern.test(firstUrl) || generalUrlPattern.test(firstUrl);
    }

    // Get the value for a specific file
    getFileValue(file: any): string {
        return this.fileValues.get(file.url) || '';
    }

    // Set the value for a specific file
    setFileValue(file: any, value: string): void {
        console.log('Setting value for', file.name, ':', value);
        this.fileValues.set(file.url, value);
    }

    // Get array of selected files for template iteration
    getSelectedFilesArray(): any[] {
        return Array.from(this.selectedFiles);
    }

    private path2dbpath(dbpath: string) {
        var s = dbpath.replace(/\//g, '/folders/');
        s = s.substring(0, s.length - 8);
        var p = 'files/' + this.currentUser.uid + s;
        return p;
    }

    public loadFiles(url: string) {
        this.curpath = url;

        // Load folders first
        var folders: any[] = [];
        this.afs.collection(this.path2dbpath(url) + 'folders').valueChanges({ idField: 'id' })
            .subscribe(f => {
                folders = f as any[];

                // Then load files
                this.afs.collection(this.path2dbpath(url) + 'files').valueChanges({ idField: 'id' })
                    .subscribe(files => {
                        // Combine and filter for images only
                        var allItems = folders.concat(files as any[]);
                        var imageItems = allItems.filter(item => this.isImageFile(item));

                        this.dataSource.data = imageItems;
                        this.dataSource.paginator = this.paginator;
                        this.dataSource.sort = this.sort;
                        this.curfiles = imageItems.filter(item => item.typ === 'file');

                        // Auto-select files that have existing values
                        this.autoSelectExistingFiles();
                    });
            });
    }

    private autoSelectExistingFiles() {
        // Auto-select files that already have values set
        this.curfiles.forEach(file => {
            if (this.fileValues.has(file.url)) {
                this.selectedFiles.add(file);
            }
        });
    }

    private isImageFile(item: any): boolean {
        if (item.typ === 'folder') return true; // Always show folders for navigation
        if (item.typ !== 'file') return false;

        const name = item.name?.toLowerCase() || '';
        return name.endsWith('.png') || name.endsWith('.jpg') ||
            name.endsWith('.jpeg') || name.endsWith('.svg') ||
            name.endsWith('.gif') || name.endsWith('.webp');
    }

    public changeFolder(url: string) {
        this.loadFiles(url);
    }

    public goBack() {
        if (this.curpath !== '/') {
            // Navigate to parent folder
            const pathParts = this.curpath.split('/').filter(p => p.length > 0);
            pathParts.pop(); // Remove current folder
            this.curpath = '/' + pathParts.join('/') + (pathParts.length > 0 ? '/' : '');
            this.loadFiles(this.curpath);
        }
    }

    public toggleFileSelection(file: any) {
        if (file.typ !== 'file') return;

        // Find if file is already selected by URL
        const existingFile = Array.from(this.selectedFiles).find(f => f.url === file.url);

        if (existingFile) {
            this.selectedFiles.delete(existingFile);
            // Keep the value in fileValues map in case user reselects
        } else {
            this.selectedFiles.add(file);
            // Initialize with empty value if not already set
            if (!this.fileValues.has(file.url)) {
                this.fileValues.set(file.url, '');
            }
        }
    }

    public isFileSelected(file: any): boolean {
        return Array.from(this.selectedFiles).some(f => f.url === file.url);
    }

    public deselectAll() {
        this.selectedFiles.clear();
        // Optionally clear values too: this.fileValues.clear();
    }

    public onApply() {
        // Check how the dialog was called to determine return format
        if (this.data?.returnFormat === 'selectOptions') {
            // For GUI component - return structured options
            const selectedOptions = Array.from(this.selectedFiles).map(file => {
                return {
                    name: file.url,
                    wert: this.fileValues.get(file.url) || ''
                };
            });

            this.dialogRef.close({
                success: true,
                options: selectedOptions
            });
        } else {
            // For editor component - return URLs with values in parentheses
            const selectedItems = Array.from(this.selectedFiles).map(file => {
                const value = this.fileValues.get(file.url) || '';
                // ALWAYS include parentheses with value, even if empty
                return `${file.url}(${value})`;
            });

            const result = selectedItems.join(';');
            console.log('Dialog result:', result);
            this.dialogRef.close(result);
        }
    }



    public onCancel() {
        this.dialogRef.close(null);
    }

    public backgroundUploaded(event: any) {
        this.afs.collection(this.path2dbpath(this.curpath) + 'files/').add({
            name: event.name,
            typ: 'file',
            path: this.curpath,
            url: event.url,
            uploadtime: new Date().getTime()
        }).then(x => {
            console.log('Image uploaded', x);
            this.snackBar.open('Image uploaded successfully', 'Close', { duration: 3000 });
            // Reload files to show the new upload
            this.loadFiles(this.curpath);
        });
    }

    public uploadFailed(event: any) {
        this.snackBar.open('Upload failed: ' + event, 'Close', { duration: 5000 });
    }
} 